import React, { CSSProperties, useCallback } from 'react';
import AntdPagination from 'antd/lib/pagination';
import { SimplePagination } from './SimplePagination';
import { trackEvent } from '@unifocus/js-utils';

/**
 * {@docCategory Pagination}
 */
export interface PaginationProps {

   /**
    * Whether or not to use simple mode.  This mode
    * is great for mobile.
    *
    * @default false
    */
   simple?: boolean;

   /**
    * The current page.
    */
   current?: number;

   /**
    * The default current page.
    *
    * @defaultvalue 1
    */
   defaultCurrent?: number;

   /**
    * The total number of items.
    */
   total?: number;

   /**
    * The number of items per page.
    * @defaultvalue 1
    */
   pageSize?: number;

   /**
    * Specify the size of Pagination, can be set to small
    * @defaultvalue default
    */
   size?: 'default' | 'small';

   /**
    * Disable the component.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Show the quick jumper.
    * This property is ignored in simple mode.
    *
    * @defaultvalue false
    */
   showQuickJumper?: boolean;

   /**
    * Called when the current page is changed.
    */
   onChange?: (page: number, pageSize?: number) => void;

   /**
    * Sets a className on the outer panel.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    */
   style?: CSSProperties;

   /**
    * If this is set, it enables automatic analytics for the pagination.  Set this to the name
    * of the component being paginated, using spaces between words and capitalizing the first letter
    * of each word.  For example:
    * Employee Maintenance Table
    */
   analyticsName?: string;
}

export const Pagination = (props: PaginationProps) => {
   const { onChange, total, analyticsName } = props;

   const onChangeHandler = useCallback((page: number, pageSize?: number)=>{
      if (onChange) {
         onChange(page, pageSize);
      }
      if (analyticsName) {
         trackEvent("Navigation",
                    "Page To",
                    analyticsName+" "+page + (total ? ("/" + total) : ""));
      }
   },[onChange, total, analyticsName]);

   if (props.simple) {
      return (<SimplePagination
         className={props.className}
         style={props.style}
         current={props.current}
         defaultCurrent={props.defaultCurrent}
         total={props.total}
         pageSize={props.pageSize || 1}
         disabled={props.disabled}
         size={props.size}
         showQuickJumper={props.showQuickJumper}
         onChange={props.onChange}
         analyticsName={props.analyticsName}
      />);
   }

   return (
      <AntdPagination
         className={`uf-pagination ${props.className || ''}`}
         style={props.style}
         current={props.current}
         defaultCurrent={props.defaultCurrent}
         total={props.total}
         pageSize={props.pageSize || 1}
         disabled={props.disabled}
         size={props.size}
         showQuickJumper={props.showQuickJumper}
         showSizeChanger={false}
         onChange={onChangeHandler}
      />
   );
};
